import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { MiPlaceholderList } from "@miplatform/components";
import { HTTP_STATUSES } from "@miplatform/constants";
import { IMaterialsResponse, ITransferListItem } from "@miplatform/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { TransferList } from "@mibuildweb/components";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import { ConfigAccordionHeader } from "../../../../../Config/components/ConfigAccordionHeader/ConfigAccordionHeader";
import { ICustomerItemsAssignPayload } from "../../CustomerPricingDetails/CustomerPricingDetails.types";
import {
  useCustomerPricingDetailsContext,
  useTabFormRegistration,
} from "../../CustomerPricingDetails/CustomerPricingDetailsProvider";
import { customerItemsAssignTabSchema } from "../../schema/customerPricingDetails.schema";
import { useCustomerItemsAssignTabViewModel } from "./useCustomerItemsAssignTabViewModel";

export function CustomerItemsAssignTab() {
  const [selectedItemsByCategory, setSelectedItemsByCategory] = useState<
    Record<string, ITransferListItem[]>
  >({});

  const { classes } = useStyles();
  const { setSaveHandler, accountDetails, setConfirmModalConfig } =
    useCustomerPricingDetailsContext();

  const {
    materials,
    groupedMaterials,
    customerMaterials,
    groupedCustomerMaterials,
    categories,
    categoryTabs,
    selectedTab,
    handleGetMaterials,
    handleGetCustomerMaterials,
    handleUpdateCustomerMaterials,
    handleGetCategories,
    handleTabChange,
    isLoading,
  } = useCustomerItemsAssignTabViewModel();

  const form = useForm<ICustomerItemsAssignPayload>({
    mode: "onChange",
    defaultValues: {},
    resolver: zodResolver(customerItemsAssignTabSchema),
  });
  const { setValue, getValues, reset } = form;

  useTabFormRegistration(form, isLoading);

  useEffect(() => {
    const fetchData = async () => {
      await handleGetMaterials();
      await handleGetCategories();
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (accountDetails) {
      handleGetCustomerMaterials(accountDetails.id);
    }
  }, [accountDetails]);

  useEffect(() => {
    setSaveHandler(async () => {
      const materialIdsByCategory = getValues();
      const selectedMaterialIds = Object.values(materialIdsByCategory)
        .flat()
        .filter(Boolean);

      const accountName = accountDetails?.name || "this account";
      const id = accountDetails?.id ?? "";

      const response = await handleUpdateCustomerMaterials(
        id,
        [...new Set(selectedMaterialIds)],
        {
          success: { text: `Successfully assigned items to ${accountName}.` },
        }
      );

      if (response.status === HTTP_STATUSES.OK) {
        reset(getValues());
      }

      return response;
    });

    if (!customerMaterials.length) {
      setConfirmModalConfig(null);
      return;
    }

    setConfirmModalConfig({
      description: `You are about to update items assigned to ${
        accountDetails?.name || "this account"
      }. All removed items will have their price values reset. 
      Are you sure you would like to continue?`,
    });
  }, [accountDetails, customerMaterials]);

  const mapMaterialToTransferListItem = (
    material: IMaterialsResponse,
    categoryAttributeIds: string[]
  ): ITransferListItem => {
    const title = `${material.manufacturerPartNumber} | ${material.category.itemDescription}`;
    const materialAttributesById = material.attributes.reduce<
      Record<string, string>
    >((acc, attribute) => {
      acc[attribute.attributeId] = attribute.value;
      return acc;
    }, {});

    const groupByOptions = categoryAttributeIds.reduce((acc, attributeId) => {
      acc[attributeId] = materialAttributesById[attributeId] || "";
      return acc;
    }, {} as Record<string, string>);

    return {
      key: material.id ?? material.materialId ?? "",
      title,
      name: title,
      description: material.manufacturerPartNumber,
      groupByOptions,
    };
  };

  const allItemsByCategory: Record<string, ITransferListItem[]> =
    useMemo(() => {
      const categoryAttributeIdsByCategoryId: Record<string, string[]> =
        categories.reduce((acc: Record<string, string[]>, category) => {
          acc[category.id] = (category.attributes || []).map(
            (attribute) => attribute.id
          );
          return acc;
        }, {});

      return Object.entries(groupedMaterials).reduce(
        (
          acc: Record<string, ITransferListItem[]>,
          [categoryId, materialsByCategory]
        ) => {
          const categoryAttributeIds =
            categoryAttributeIdsByCategoryId[categoryId] || [];

          acc[categoryId] = materialsByCategory.map((material) =>
            mapMaterialToTransferListItem(material, categoryAttributeIds)
          );
          return acc;
        },
        {}
      );
    }, [categories, groupedMaterials]);

  useEffect(() => {
    if (!Object.keys(allItemsByCategory).length) {
      return;
    }

    const selectedByCategory = Object.entries(groupedCustomerMaterials).reduce(
      (acc, [categoryId, materialsByCategory]) => {
        const selectedIds = new Set(
          materialsByCategory
            .map((material) => material.id ?? material.materialId ?? "")
            .filter(Boolean)
        );

        const selectedTransferItems = (
          allItemsByCategory[categoryId] ?? []
        ).filter((item) => selectedIds.has(item.key));

        if (selectedTransferItems.length > 0) {
          acc[categoryId] = selectedTransferItems;
        }

        return acc;
      },
      {} as Record<string, ITransferListItem[]>
    );

    setSelectedItemsByCategory(selectedByCategory);

    const formValues = Object.entries(selectedByCategory).reduce(
      (acc, [categoryId, items]) => {
        acc[categoryId] = items.map((item) => item.key);
        return acc;
      },
      {} as ICustomerItemsAssignPayload
    );

    reset(formValues);
  }, [allItemsByCategory, groupedCustomerMaterials]);

  const syncCategorySelectionToForm = (
    categoryId: string,
    items: ITransferListItem[]
  ) => {
    setValue(
      categoryId,
      items.map((item) => item.key),
      {
        shouldDirty: true,
        shouldValidate: true,
      }
    );
  };

  const handleAddItem = (itemKey: string) => {
    if (!selectedTab) return;

    const categoryId = selectedTab.id;
    const item = (allItemsByCategory[categoryId] ?? []).find(
      (candidate) => candidate.key === itemKey
    );

    if (!item) return;

    setSelectedItemsByCategory((prev) => {
      const existingCategoryItems = prev[categoryId] ?? [];
      if (existingCategoryItems.some((existing) => existing.key === itemKey)) {
        return prev;
      }

      const newCategoryItems = [...existingCategoryItems, item];
      syncCategorySelectionToForm(categoryId, newCategoryItems);

      return {
        ...prev,
        [categoryId]: newCategoryItems,
      };
    });
  };

  const handleRemoveItem = (itemKey: string) => {
    if (!selectedTab) return;

    const categoryId = selectedTab.id;

    setSelectedItemsByCategory((prev) => {
      const existingCategoryItems = prev[categoryId] ?? [];
      const newCategoryItems = existingCategoryItems.filter(
        (item) => item.key !== itemKey
      );

      syncCategorySelectionToForm(categoryId, newCategoryItems);

      return {
        ...prev,
        [categoryId]: newCategoryItems,
      };
    });
  };

  const availableItems = useMemo(() => {
    if (!selectedTab) return [];

    return allItemsByCategory[selectedTab.id] ?? [];
  }, [allItemsByCategory, selectedTab?.id]);

  const selectedItems = useMemo(() => {
    if (!selectedTab) return [];

    return selectedItemsByCategory[selectedTab.id] ?? [];
  }, [selectedItemsByCategory, selectedTab?.id]);

  const groupBySelectOptions = useMemo(() => {
    if (!selectedTab) return [];

    const selectedCategory = categories.find(
      (category) => category.id === selectedTab.id
    );

    return (selectedCategory?.attributes || []).map((attribute) => ({
      id: attribute.id,
      label: attribute.name,
    }));
  }, [categories, selectedTab]);

  const isEmpty = materials.length === 0 && categories.length === 0;

  return (
    <ConfigAccordionHeader title="Assign Items to Customer" disableCollapse>
      {isEmpty && (
        <Box className={classes.placeholderBox}>
          <MiPlaceholderList
            text="No items available."
            helperText="Items must be configured before they can be assigned."
          />
        </Box>
      )}
      {!isEmpty && (
        <Box height="100%" display="flex" flexDirection="column">
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Select all items per category which this customer uses.
          </Typography>
          {categoryTabs.length > 0 && (
            <Tabs
              variant="scrollable"
              scrollButtons="auto"
              value={selectedTab?.id ?? false}
              onChange={(_, value) => {
                const tab = categoryTabs.find((t) => t.id === value);
                if (tab) handleTabChange(tab);
              }}
              sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
            >
              {categoryTabs.map((tab) => (
                <Tab
                  key={tab.id}
                  value={tab.id}
                  label={tab.name}
                  sx={{ textTransform: "none" }}
                />
              ))}
            </Tabs>
          )}

          <TransferList
            availableItems={availableItems}
            selectedItems={selectedItems}
            availableTitle="Available Items"
            selectedTitle="Selected Items"
            showAvailableItemsSearch
            showSelectedItemsSearch
            disabled={isLoading}
            groupBySelectOptions={groupBySelectOptions}
            emptySelectedListMessage="Selected items will show here."
            onAddItem={handleAddItem}
            onRemoveItem={handleRemoveItem}
            renderItemTitle={(item: ITransferListItem) => (
              <Typography variant="HD2" color="primary">
                {item.title}
              </Typography>
            )}
          />
        </Box>
      )}
    </ConfigAccordionHeader>
  );
}

const useStyles = makeStyles()((theme) => ({
  placeholderBox: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2, 0),
  },
}));
